import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SpecialOfferAPIService } from 'src/app/extensions/services/api/specialOffer-api.service';
import { FileStoreAPIService } from 'src/app/extensions/services/api/file-store-api.service';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { SIGNAL_ITEM_KEY, SPECIAL_OFFER_STATUS } from 'src/app/extensions/constants/commonConstants';
import { SpecialOfferPopupComponent } from 'src/app/extensions/components/special-offer-popup/special-offer-popup.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NotificationService } from 'src/app/core/services/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, switchMap } from 'rxjs';
import { SpecialOfferEditDatesModalComponent } from '../../special-offer-modals/special-offer-edit-dates-modal/special-offer-edit-dates-modal.component';
import { SpecialOfferEditFilesModalComponent } from '../../special-offer-modals/special-offer-edit-files-modal/special-offer-edit-files-modal.component';
import { SpecialOfferEditSegmentationModalComponent } from '../../special-offer-modals/special-offer-edit-segmentations-modal/special-offer-edit-segmentation-modal.component';
import { CountdownService } from 'src/app/extensions/services/countdown-service.service';
import { Router } from '@angular/router';
import { currentTimezoneDateTime, isNotEmpty } from 'src/app/extensions/utilities';
import { SPECIAL_OFFER_ROUTES } from 'src/app/core/constants/routes-constants';

@Component({
  selector: 'preview-information',
  templateUrl: './preview-information.component.html',
  styleUrls: ['./preview-information.component.scss'],
  standalone: false,
})
export class PreviewInformationComponent implements OnInit {
  @Input() specialOfferId: string;
  @Output() formChanged: EventEmitter<boolean> = new EventEmitter();
  @Output() unloadForm: EventEmitter<boolean> = new EventEmitter();
  @Output() setSpecialOfferStatus: EventEmitter<string> = new EventEmitter();
  public specialOffer: app.models.GetSpecialOfferModel | null = null;
  public specialOfferStatus = SPECIAL_OFFER_STATUS;
  stateSignalItem: any;
  countdown$: Observable<string> = of('');
  public isFormDirty = false;
  specialOfferRoutes = SPECIAL_OFFER_ROUTES;

  constructor(
    private readonly _globalService: GlobalService,
    private _specialOfferAPIService: SpecialOfferAPIService,
    private _fileStoreAPIService: FileStoreAPIService,
    private _router: Router,
    private readonly _ngbModal: NgbModal,
    private _notificationService: NotificationService,
    private _translateService: TranslateService,
    private _countdownService: CountdownService
  ) {}

  ngOnInit(): void {
    this.stateSignalItem = this._globalService.getSignalItem(SIGNAL_ITEM_KEY.SPECIAL_OFFER_DATA);
    if (
      (isNotEmpty(this.stateSignalItem) && this.stateSignalItem.data.specialOfferId === this.specialOfferId) ||
      (isNotEmpty(this.stateSignalItem) &&
        this.stateSignalItem.data.specialOfferId === this.specialOfferId &&
        this.stateSignalItem.data.isNewCamapign) ||
      (isNotEmpty(this.stateSignalItem) &&
        this.stateSignalItem.data.specialOfferId === this.specialOfferId &&
        this.stateSignalItem.data.isDuplicatedCampaign)
    ) {
      this.specialOffer = this.stateSignalItem.data;
      if (this.stateSignalItem.data.isNewCamapign) {
        this.specialOffer.status = this.specialOfferStatus.DRAFT;
      }
      if (this.specialOffer.endDate) {
        this.countdown$ = this._countdownService.getCountdown(this.specialOffer.endDate);
      }

      this.isFormDirty =
        this.stateSignalItem.data.isCampaignDescriptionFormDirty ||
        this.stateSignalItem.data.isCampaignDetailsFormDirty ||
        this.stateSignalItem.data.isClientSegmentaionFormDirty ||
        this.stateSignalItem.data.isFinancialDetailsFormDirty;

      this.formChanged.emit(this.isFormDirty);

      if (this.specialOffer.campaignImageDocument?.documentStoragePath) {
        this._fileStoreAPIService
          .getFile(this.specialOffer.campaignImageDocument.documentStoragePath)
          .subscribe((fileContent: string) => {
            this.specialOffer.campaignImageSrc = `data:image/png;base64,${fileContent}`;
          });
      }
    } else {
      this._globalService.setSignalItem(SIGNAL_ITEM_KEY.SPECIAL_OFFER_DATA, undefined);
      if (this.specialOfferId) {
        this._specialOfferAPIService
          .getSpecialOffer(this.specialOfferId)
          .pipe(
            switchMap(response => {
              if (response.campaignImageDocument?.documentStoragePath) {
                this._fileStoreAPIService
                  .getFile(response.campaignImageDocument.documentStoragePath)
                  .subscribe((fileContent: string) => {
                    this.specialOffer.campaignImageSrc = `data:image/png;base64,${fileContent}`;
                  });
              }
              return of(response);
            })
          )
          .subscribe(response => {
            this.specialOffer = {
              ...response,
              startDate: new Date(response.startDate),
              endDate: new Date(response.endDate),
            };
            this.setSpecialOfferStatus.emit(this.specialOffer.status);

            this.stateSignalItem = { data: this.specialOffer };
            this._globalService.setSignalItem(SIGNAL_ITEM_KEY.SPECIAL_OFFER_DATA, this.stateSignalItem);

            if (this.specialOffer.endDate) {
              this.countdown$ = this._countdownService.getCountdown(this.specialOffer.endDate);
            }
          });
      }
    }
  }

  public editSection(section: string, status?: string) {
    if (status === this.specialOfferStatus.ONGOING) {
      if (section === this.specialOfferRoutes.CAMPAIGN_DETAILS) {
        const modal = this._ngbModal.open(SpecialOfferEditDatesModalComponent, { centered: true });

        modal.componentInstance.startDate = this.specialOffer.startDate;
        modal.componentInstance.endDate = this.specialOffer.endDate;

        modal.result.then(result => {
          if (result) {
            this.specialOffer.endDate = new Date(result);
            this.countdown$ = this._countdownService.getCountdown(this.specialOffer.endDate);
            let campaign: app.models.SaveSpecialOfferModel = {
              ...this.specialOffer,
              campaignImage: this.specialOffer.campaignImageDocument,
              startDate: currentTimezoneDateTime(this.specialOffer.startDate),
              endDate: currentTimezoneDateTime(this.specialOffer.endDate),
            };
            this.saveRequestHandler(campaign, false);
          }
        });
      }
      if (section === this.specialOfferRoutes.CAMPAIGN_DESCRIPTION) {
        const modal = this._ngbModal.open(SpecialOfferEditFilesModalComponent, { centered: true });

        modal.result.then(result => {
          if (result) {
            this.specialOffer.campaignDocuments.push(...result);
            let campaign: app.models.SaveSpecialOfferModel = {
              ...this.specialOffer,
              campaignImage: this.specialOffer.campaignImageDocument,
              startDate: currentTimezoneDateTime(this.specialOffer.startDate),
              endDate: currentTimezoneDateTime(this.specialOffer.endDate),
            };
            this.saveRequestHandler(campaign, false);
          }
        });
      }
      if (section === this.specialOfferRoutes.CLIENT_SEGMANTATION) {
        const modal = this._ngbModal.open(SpecialOfferEditSegmentationModalComponent, { centered: true });
        modal.componentInstance.segmentationData = {
          dictionarySegmentItemTypeExternalEntityId: this.specialOffer.dictionarySegmentItemTypeExternalEntityId,
          maximumPaymentDelay: this.specialOffer.maximumPaymentDelay,
          periodUntilTermination: this.specialOffer.periodUntilTermination,
          segmentItems: this.specialOffer.segmentItems,
          segmentItemType: this.specialOffer.segmentItemType,
          campaignType: this.specialOffer.campaignType,
        };

        modal.result.then(result => {
          if (result) {
            this.specialOffer = { ...this.specialOffer, ...result };
            let campaign: app.models.SaveSpecialOfferModel = {
              ...this.specialOffer,
              campaignImage: this.specialOffer.campaignImageDocument,
              startDate: currentTimezoneDateTime(this.specialOffer.startDate),
              endDate: currentTimezoneDateTime(this.specialOffer.endDate),
            };
            this.saveRequestHandler(campaign, false);
          }
        });
      }
    } else {
      this.unloadForm.emit(true);
      this._router.navigate([
        SPECIAL_OFFER_ROUTES.ADMIN_SPECIAL_OFFERS,
        SPECIAL_OFFER_ROUTES.NEW,
        `${section}`,
        `${this.specialOffer.specialOfferId ?? ''}`,
      ]);
    }
  }

  public downloadDocument(file: app.models.SpecialOfferXDocument): void {
    if (file.documentStoragePath) {
      this._fileStoreAPIService.getFile(file.documentStoragePath).subscribe(response => {
        if (response) {
          const documentRef = `data:application/pdf;base64,${response}`;
          const downloadLink = document.createElement('a');
          downloadLink.href = documentRef;
          downloadLink.download = file.documentName;
          downloadLink.click();
        }
      });
    }
  }

  public downloadAllDocuments(): void {
    this.specialOffer.campaignDocuments.forEach(document => {
      this.downloadDocument(document);
    });
  }

  publishCampaign(status: string) {
    let campaign: app.models.SaveSpecialOfferModel = {
      ...this.specialOffer,
      status: status,
      campaignImage: this.specialOffer.campaignImageDocument,
      specialOfferId: this.specialOfferId,
      startDate: currentTimezoneDateTime(this.specialOffer.startDate),
      endDate: currentTimezoneDateTime(this.specialOffer.endDate),
    };

    if (status === this.specialOfferStatus.DRAFT || status === this.specialOfferStatus.UPCOMING) {
      this.saveRequestHandler(campaign, true);
    } else {
      const modal = this._ngbModal.open(SpecialOfferPopupComponent, {
        centered: true,
        windowClass: 'custom-special-offers-modal',
      });

      modal.componentInstance.title = 'specialOffers.wizard.publishCampaign.title';
      modal.result.then(result => {
        if (result) {
          this.saveRequestHandler(campaign, true);
        }
      });
    }
  }

  private saveRequestHandler(campaign, redirect: boolean) {
    this._specialOfferAPIService.saveSpecialOffer(campaign).subscribe(result => {
      if (result.isSuccess) {
        this._notificationService.showSuccess(
          this._translateService.instant('specialOffers.wizard.notification.requestSaved'),
          this._translateService.instant('common.success')
        );
        this._globalService.setSignalItem(SIGNAL_ITEM_KEY.SPECIAL_OFFER_DATA, undefined);
        this.formChanged.emit(false);
        if (redirect) {
          this._router.navigate([
            SPECIAL_OFFER_ROUTES.ADMIN_SPECIAL_OFFERS,
            SPECIAL_OFFER_ROUTES.NEW,
            SPECIAL_OFFER_ROUTES.CAMPAIGN_PUBLISHED,
          ]);
        }
      }
    });
  }

  deleteCampaign() {
    const modal = this._ngbModal.open(SpecialOfferPopupComponent, {
      centered: true,
      windowClass: 'custom-special-offers-modal',
    });
    modal.componentInstance.title = 'specialOffers.wizard.deleteCampaign.title';
    modal.result.then(result => {
      if (result) {
        this._specialOfferAPIService.deleteSpecialOffer(this.specialOfferId).subscribe(res => {
          if (res.isSuccess) {
            this._notificationService.showSuccess(
              this._translateService.instant('specialOffers.wizard.notification.requestDeleted'),
              this._translateService.instant('common.success')
            );
            this._router.navigate([SPECIAL_OFFER_ROUTES.ADMIN_SPECIAL_OFFERS]);
          }
        });
      }
    });
  }

  public getFileTypeIcon(filename: string): string {
    const extension = filename.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return 'file-pdf';
      case 'doc':
      case 'docx':
        return 'file-word';
      default:
        return 'file-pdf';
    }
  }

  duplicateCampaign() {
    let duplicatedSpecialOffer: app.models.GetSpecialOfferModel;
    this._specialOfferAPIService
      .getSpecialOffer(this.specialOfferId)
      .pipe(
        switchMap(response => {
          if (response.campaignImageDocument?.documentStoragePath) {
            this._fileStoreAPIService
              .getFile(response.campaignImageDocument.documentStoragePath)
              .subscribe((fileContent: string) => {
                duplicatedSpecialOffer.campaignImageSrc = `data:image/png;base64,${fileContent}`;
              });
          }
          return of(response);
        })
      )
      .subscribe(response => {
        duplicatedSpecialOffer = {
          ...response,
          startDate: undefined,
          endDate: undefined,
          specialOfferId: '',
          status: this.specialOfferStatus.DRAFT,
        };

        this.stateSignalItem = { data: { ...duplicatedSpecialOffer, isDuplicatedCampaign: true, isNewCamapign: true } };
        this._globalService.setSignalItem(SIGNAL_ITEM_KEY.SPECIAL_OFFER_DATA, this.stateSignalItem);

        this._router.navigate([
          SPECIAL_OFFER_ROUTES.ADMIN_SPECIAL_OFFERS,
          SPECIAL_OFFER_ROUTES.NEW,
          SPECIAL_OFFER_ROUTES.PREVIEW,
          '',
        ]);
      });
  }

  stopCampaign() {
    const modal = this._ngbModal.open(SpecialOfferPopupComponent, { centered: true });
    modal.componentInstance.title = 'specialOffers.wizard.stopCampaign.title';
    modal.result.then(result => {
      if (result) {
        this._specialOfferAPIService.stopCampaign(this.specialOffer.specialOfferId).subscribe(response => {
          if (response.isSuccess) {
            this._notificationService.showSuccess(
              this._translateService.instant('specialOffers.wizard.notification.requestStopped'),
              this._translateService.instant('common.success')
            );
            this._router.navigate([SPECIAL_OFFER_ROUTES.ADMIN_SPECIAL_OFFERS]);
          }
        });
      }
    });
  }
}
